/**
 * HYPSEYA — Somatic Check-in
 * 30-second 5-zone body scan modal.
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth0 as useAuth0Real } from '@auth0/auth0-react';
import { useAuth0 as useAuth0Mock } from '@/MockAuth0Provider';
import { Wave, Meditate, Cycle, Stable, Leaf } from '@/components/HypIcons';
const useAuth0 = process.env.REACT_APP_DEV_BYPASS_AUTH === 'true' ? useAuth0Mock : useAuth0Real;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ZONES = [
  { id: 'head',  label: 'Head',  Icon: Wave,     desc: 'Thoughts, facial tension, jaw' },
  { id: 'chest', label: 'Chest', Icon: Meditate, desc: 'Heart, breath, tightness' },
  { id: 'belly', label: 'Belly', Icon: Cycle,    desc: 'Stomach, solar plexus, knots' },
  { id: 'hands', label: 'Hands', Icon: Stable,   desc: 'Tension, temperature, tingling' },
  { id: 'legs',  label: 'Legs',  Icon: Leaf,     desc: 'Weight, grounding, restlessness' },
];

const LEVEL_COLORS = ['#2D8B87', '#4AACA8', '#B8D3CF', '#B8906A', '#C47B52'];
const LEVEL_LABELS = ['Soft', 'Light', 'Neutral', 'Tense', 'Intense'];

function ZoneRow({ zone, value, onChange }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <zone.Icon size={18} color="#2D8B87" />
        <span style={{ fontSize: '0.9rem', fontWeight: '700', color: '#1E3A39' }}>{zone.label}</span>
        <span style={{ fontSize: '0.72rem', color: '#AAB8B6', flex: 1 }}>{zone.desc}</span>
        <span style={{
          fontSize: '0.75rem', fontWeight: '700',
          color: LEVEL_COLORS[value - 1],
          background: `${LEVEL_COLORS[value - 1]}18`,
          padding: '2px 10px', borderRadius: 10,
        }}>{LEVEL_LABELS[value - 1]}</span>
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        {[1, 2, 3, 4, 5].map(v => (
          <button key={v} onClick={() => onChange(v)} style={{
            flex: 1, height: 32, borderRadius: 8, border: 'none',
            background: value === v ? LEVEL_COLORS[v - 1] : value > v ? `${LEVEL_COLORS[v - 1]}40` : 'rgba(184,211,207,0.2)',
            cursor: 'pointer', transition: 'all 0.15s ease',
            transform: value === v ? 'scaleY(1.15)' : 'none',
          }} />
        ))}
      </div>
    </div>
  );
}

export default function SomaticCheckin({ userId, onClose, onSave, context = 'standalone' }) {
  const { getAccessTokenSilently } = useAuth0();
  const [zones, setZones] = useState({ head: 3, chest: 3, belly: 3, hands: 3, legs: 3 });
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const overall = Math.round(Object.values(zones).reduce((s, v) => s + v, 0) / 5);

  const handleSave = async () => {
    if (!userId) { toast.error('User not found'); return; }
    setSaving(true);
    try {
      const token = await getAccessTokenSilently().catch(() => null);
      const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      await axios.post(`${API}/somatic-checkin`, { user_id: userId, zones, overall, note, context }, cfg);
      toast.success('Check-in saved ✓');
      if (onSave) onSave({ zones, overall });
      onClose();
    } catch {
      toast.error('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const overallColor = LEVEL_COLORS[overall - 1] || '#B8D3CF';

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 8000,
      background: 'rgba(10,26,25,0.75)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      backdropFilter: 'blur(4px)',
    }}>
      <div style={{
        width: '100%', maxWidth: 520,
        background: 'linear-gradient(160deg, #F7F3EC 0%, #EFE7DD 100%)',
        borderRadius: '24px 24px 0 0', padding: '28px 24px 40px',
        animation: 'hyp-slide-up 0.35s ease',
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.35rem', color: '#1E3A39', margin: '0 0 4px', fontWeight: '500' }}>
              Somatic Check-in
            </h2>
            <p style={{ margin: 0, fontSize: '0.8rem', color: '#8FA3A0' }}>30 seconds · How is your body right now?</p>
          </div>
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            background: `${overallColor}18`, border: `1px solid ${overallColor}40`,
            borderRadius: 14, padding: '8px 14px', flexShrink: 0,
          }}>
            <span style={{ fontSize: '1.4rem', fontWeight: '700', color: overallColor, fontFamily: "'Playfair Display', serif" }}>{overall}</span>
            <span style={{ fontSize: '0.62rem', color: '#AAB8B6', fontWeight: '600', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Overall</span>
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          {ZONES.map(z => (
            <ZoneRow key={z.id} zone={z} value={zones[z.id]} onChange={v => setZones(prev => ({ ...prev, [z.id]: v }))} />
          ))}
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontSize: '0.8rem', color: '#8FA3A0', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>
            Note (optional)
          </label>
          <textarea value={note} onChange={e => setNote(e.target.value)}
            placeholder="A sensation, a word, whatever feels right..."
            rows={2}
            style={{
              width: '100%', padding: '12px 14px', borderRadius: 12,
              border: '1px solid rgba(184,211,207,0.5)', background: 'rgba(255,255,255,0.7)',
              fontSize: '0.88rem', color: '#1E3A39', resize: 'none', outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={handleSave} disabled={saving} style={{
            flex: 1, padding: '14px',
            background: saving ? 'rgba(30,107,104,0.3)' : 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
            color: '#fff', border: 'none', borderRadius: 20, fontSize: '0.9rem',
            fontWeight: '600', cursor: saving ? 'default' : 'pointer',
            boxShadow: saving ? 'none' : '0 4px 16px rgba(30,107,104,0.25)',
          }}>
            {saving ? 'Saving…' : 'Save check-in'}
          </button>
          <button onClick={onClose} style={{
            padding: '14px 18px', background: 'transparent',
            border: '1px solid rgba(184,211,207,0.5)', borderRadius: 20,
            color: '#AAB8B6', fontSize: '0.85rem', cursor: 'pointer',
          }}>Close</button>
        </div>
      </div>

      <style>{`
        @keyframes hyp-slide-up {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}
